/**
 * The preview tab — a browser tab signed in as one member of a space, read-only, for an admin.
 *
 * WHY A TAB OF ITS OWN. Supabase keeps a sign-in in localStorage, which every tab on the site shares,
 * so signing in as somebody else anywhere would sign the admin out everywhere. A preview tab keeps its
 * sign-in in sessionStorage instead, under a key no other tab uses, so the admin's own tabs stay
 * signed in as them and several previews can be open side by side.
 *
 * HOW IT GETS ITS TOKEN. The admin's tab asks /api/workspace/<slug>/preview-as for a token, leaves it
 * in localStorage under a one-time key, and opens `/<slug>?preview=<id>`. The new tab moves the token
 * into its own sessionStorage and deletes the localStorage copy before anything else reads it, so the
 * token never appears in a URL and never outlives the tab.
 *
 * WHY THE AUTH CALLS ARE ANSWERED HERE. The token names no GoTrue session on purpose (that is what
 * stops it changing the person's password; see api/_shared/userToken.js). So GoTrue answers every
 * call it receives with `session_not_found`, and supabase-js reacts to that by signing the tab out.
 * The tab therefore answers "who am I" itself, from what the server sent, and refuses everything else
 * the auth API does — signing in, refreshing, changing the user — without asking GoTrue at all.
 *
 * Nothing here is a security boundary. The database decides what the token can read and that it can
 * write nothing (supabase/migrations/20261113090000_preview_as_person.sql). This module only keeps the
 * tab coherent.
 */

export const PREVIEW_QUERY_PARAM = 'preview';
const HANDOFF_PREFIX = 'arcos.preview.handoff.';
const TAB_KEY = 'arcos.preview.tab';
/** A handoff the new tab never collected is useless after this long, and is ignored. */
const HANDOFF_TTL_MS = 60 * 1000;

function safe(fn, fallback = null) {
  try { return fn(); } catch { return fallback; }
}

/**
 * Leave a preview for the tab about to be opened, and return the path to open it at.
 * @param {Window} win
 * @param {{ previewId: string, token: string, expiresAt: string, person: object, spaceSlug: string }} preview
 * @returns {string}
 */
export function handOffPreview(win, preview) {
  const record = { ...preview, handedOffAt: Date.now() };
  safe(() => win.localStorage.setItem(HANDOFF_PREFIX + preview.previewId, JSON.stringify(record)));
  return `/${encodeURIComponent(preview.spaceSlug)}?${PREVIEW_QUERY_PARAM}=${encodeURIComponent(preview.previewId)}`;
}

/**
 * The preview this tab holds, or null for an ordinary tab. Collects a handoff on the tab's first load
 * (and deletes it), then reads it from sessionStorage on every reload after.
 * @param {Window} win
 * @param {number} [now]
 * @returns {null | { previewId, token, expiresAt, person, spaceSlug, expired: boolean }}
 */
export function readPreviewTab(win, now = Date.now()) {
  if (!win) return null;
  let preview = safe(() => JSON.parse(win.sessionStorage.getItem(TAB_KEY) || 'null'));

  const id = safe(() => new URLSearchParams(win.location.search).get(PREVIEW_QUERY_PARAM));
  if (id && (!preview || preview.previewId !== id)) {
    const key = HANDOFF_PREFIX + id;
    const handed = safe(() => JSON.parse(win.localStorage.getItem(key) || 'null'));
    safe(() => win.localStorage.removeItem(key));
    if (handed?.token && now - (handed.handedOffAt || 0) <= HANDOFF_TTL_MS) {
      const { handedOffAt: _drop, ...rest } = handed;
      preview = rest;
      safe(() => win.sessionStorage.setItem(TAB_KEY, JSON.stringify(preview)));
    }
  }
  if (!preview?.token || !preview?.person?.id) return null;
  return { ...preview, expired: Date.parse(preview.expiresAt) <= now };
}

/** The sessionStorage key this tab's Supabase session lives under. Never the shared localStorage one. */
export function previewStorageKey(preview) {
  return `arcos-preview-${preview.previewId}`;
}

/** The user object supabase-js would get from GoTrue's /user, built from what the server sent. */
export function previewUser(preview) {
  const p = preview.person || {};
  return {
    id: p.id,
    aud: 'authenticated',
    role: 'authenticated',
    email: p.email || undefined,
    // The app reads a confirmed contact as "signed in" (useSession). A member who has never signed in
    // previews as they will look the moment they do — answer boxes and all — because there is no
    // version of THEM on the site before that: signing in is what claims the account. Previewing them
    // as a guest showed every per-person question read-only (EarthKind, 2026-10-09), which is what no
    // member ever sees. Only a member with no email at all, who cannot sign in, stays a guest.
    email_confirmed_at: p.claimedAt || (p.email ? new Date(0).toISOString() : undefined),
    is_anonymous: false,
    app_metadata: { provider: 'preview' },
    user_metadata: { display_name: p.displayName || undefined },
    created_at: new Date(0).toISOString(),
  };
}

/** The stored-session shape supabase-js reads back on load. */
export function previewSession(preview) {
  const expiresAt = Math.floor(Date.parse(preview.expiresAt) / 1000);
  return {
    access_token: preview.token,
    token_type: 'bearer',
    expires_at: expiresAt,
    expires_in: Math.max(0, expiresAt - Math.floor(Date.now() / 1000)),
    // Not a real refresh token: a refresh is refused below, so the tab ends when the token does.
    refresh_token: `preview-${preview.previewId}`,
    user: previewUser(preview),
  };
}

function json(status, body) {
  return new Response(body == null ? null : JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

/** Fired on window whenever the tab stops a save, so the bar can say so (PreviewTabBanner). */
export const PREVIEW_WRITE_BLOCKED_EVENT = 'arcos:preview-write-blocked';

const WRITE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/**
 * Wrap fetch for a preview tab.
 *
 * The auth API is answered locally: `GET /auth/v1/user` returns the previewed person, sign-out
 * succeeds (it only clears this tab), and every other auth call is refused.
 *
 * A write to a table (`POST`/`PATCH`/`PUT`/`DELETE` on /rest/v1, other than an RPC) is stopped here
 * and announced. The database refuses it anyway; stopping it in the tab is about the person at the
 * keyboard, because many controls show a value before the server answers and never take it back, so
 * a refused save looked like a saved one. RPCs still go to the server, which refuses any not vetted;
 * a refusal is announced the same way, since most actions that write go through one.
 */
export function previewFetch(baseFetch, preview, supabaseUrl, onBlockedWrite = () => {}) {
  const root = String(supabaseUrl).replace(/\/+$/, '');
  const authBase = `${root}/auth/v1/`;
  const restBase = `${root}/rest/v1/`;
  return async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || String(input);
    const method = String(init.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
    if (url.startsWith(restBase) && WRITE_METHODS.has(method) && !url.startsWith(`${restBase}rpc/`)) {
      onBlockedWrite({ method, table: url.slice(restBase.length).split('?')[0] });
      return json(405, {
        code: '25006',
        message: 'This is a read-only preview. Nothing was saved.',
        hint: 'preview_read_only',
      });
    }
    if (url.startsWith(`${restBase}rpc/`)) {
      const res = await baseFetch(input, init);
      if (res.status === 403) {
        const body = await res.clone().json().catch(() => null);
        if (body?.hint === 'preview_read_only') onBlockedWrite({ method, rpc: url.slice(restBase.length + 4).split('?')[0] });
      }
      return res;
    }
    if (!url.startsWith(authBase)) return baseFetch(input, init);
    const path = url.slice(authBase.length).split('?')[0];
    if (path === 'user' && method === 'GET') return json(200, previewUser(preview));
    if (path === 'logout') return json(204, null);
    return json(403, {
      code: 403,
      error_code: 'preview_read_only',
      msg: 'This tab is a read-only preview. Nothing here can sign in or change an account.',
    });
  };
}

/**
 * createClient options for a preview tab. Writes the session into this tab's sessionStorage first, so
 * supabase-js finds it there on load and never touches the shared localStorage sign-in.
 */
export function previewClientOptions(win, preview, supabaseUrl, headers = {}) {
  const storageKey = previewStorageKey(preview);
  const storage = {
    getItem: (k) => safe(() => win.sessionStorage.getItem(k)),
    setItem: (k, v) => safe(() => win.sessionStorage.setItem(k, v)),
    removeItem: (k) => safe(() => win.sessionStorage.removeItem(k)),
  };
  if (!preview.expired) storage.setItem(storageKey, JSON.stringify(previewSession(preview)));
  else storage.removeItem(storageKey);
  return {
    auth: {
      storage,
      storageKey,
      persistSession: true,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers,
      fetch: previewFetch((...args) => win.fetch(...args), preview, supabaseUrl,
        (detail) => {
          // Named in the console so a refusal can be traced to what asked. An RPC here may be a read the
          // preview has not vetted yet (preview_allowed_rpcs), not a save.
          safe(() => win.console?.info?.('[preview] refused:', detail));
          safe(() => win.dispatchEvent(new win.CustomEvent(PREVIEW_WRITE_BLOCKED_EVENT, { detail })));
        }),
    },
  };
}
